/**
 * Navigation Section Component
 * Collapsible section grouping for navigation items
 */

import { useState } from 'react';
import * as Icons from 'lucide-react';
import { NavigationTreeNode, NavigationBadge } from '../../types/navigation.types';
import { NavigationItem } from './NavigationItem';
import { useNavigationStore } from '../../store/navigationStore';
import { cn } from '../../lib/utils';

interface NavigationSectionProps {
  id: string;
  label: string;
  subtitle?: string;
  subBadge?: string;
  icon?: string;
  /** Tailwind bg classes for the icon pod, e.g. 'bg-green-100 dark:bg-green-900/30' */
  iconBg?: string;
  /** Tailwind text-color classes for the icon inside the pod, e.g. 'text-green-700 dark:text-green-400' */
  iconColor?: string;
  items: NavigationTreeNode[];
  defaultExpanded?: boolean;
  collapsible?: boolean;
  badge?: NavigationBadge;
}

export function NavigationSection({
  id,
  label,
  subtitle,
  subBadge,
  icon,
  iconBg,
  iconColor,
  items,
  defaultExpanded = true,
  collapsible = true,
  badge,
}: NavigationSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { toggleSection, collapsedSections } = useNavigationStore();

  // Get icon component
  const IconComponent = icon
    ? (Icons[icon as keyof typeof Icons] as any)
    : null;

  // Don't render if no visible items
  if (items.length === 0) {
    return null;
  }

  const handleToggle = () => {
    if (!collapsible) return;

    const opening = !isExpanded;
    setIsExpanded(opening);

    // When opening the section, collapse any child items that have sub-items
    // and are currently expanded — so sub-menus always start closed.
    if (opening) {
      for (const item of items) {
        if (item.hasVisibleChildren && !collapsedSections.includes(item.id)) {
          toggleSection(item.id);
        }
      }
    }
  };

  return (
    <div className="space-y-2">
      {/* Section Header */}
      <button
        onClick={handleToggle}
        className={cn(
          'w-full flex items-start gap-2 px-2 py-1.5 rounded-lg',
          'text-sm font-semibold',
          'text-gray-700 dark:text-gray-300',
          collapsible && 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100',
          !collapsible && 'cursor-default'
        )}
        aria-expanded={isExpanded}
        aria-controls={`${id}-content`}
        disabled={!collapsible}
      >
        {/* Icon pod — tinted square when iconBg is provided, plain otherwise */}
        {IconComponent && (
          iconBg ? (
            <span className={cn(
              'w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5',
              iconBg
            )}>
              <IconComponent className={cn('w-3.5 h-3.5', iconColor)} aria-hidden="true" />
            </span>
          ) : (
            <IconComponent className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          )
        )}

        {/* Label & optional sub-line */}
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate">{label}</span>
          </div>
          {subBadge && (
            <div className="mt-0.5">
              <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium leading-none rounded bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                {subBadge}
              </span>
            </div>
          )}
          {subtitle && (
            <p className="text-[10px] font-normal text-gray-400 dark:text-gray-500 leading-tight mt-0.5">
              {subtitle}
            </p>
          )}
        </div>

        {/* Badge (top level) */}
        {badge && (
          <span
            className={cn(
              'px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0',
              badge.color || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
            )}
          >
            {badge.value}
          </span>
        )}

        {/* Expand/Collapse Icon */}
        {collapsible && (
          <Icons.ChevronDown
            className={cn(
              'w-4 h-4 flex-shrink-0 transition-transform duration-200 mt-0.5',
              !isExpanded && '-rotate-90'
            )}
            aria-hidden="true"
          />
        )}
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div
          id={`${id}-content`}
          className="space-y-1"
          role="group"
          aria-label={label}
        >
          {items.map((item) => (
            <NavigationItem key={item.id} node={item} depth={0} />
          ))}
        </div>
      )}
    </div>
  );
}

// Made with Bob
